import type { QuotaIncreaseRequestView, QuotaIncreaseStatus, QuotaScope } from "@starsach/shared-types";

/**
 * #495: "per instance" or "per account" - never the instance's name, since an
 * environment is one instance and the page is already its. A row from a stack
 * that read account-level values only has no scope and gets no label.
 */
export function quotaScopeLabel(scope: QuotaScope | undefined, resourceType?: string): string | undefined {
  if (!scope) return undefined;
  if (scope === "account") return "per account";
  if (resourceType === "AWS::Connect::Queue") return "per queue";
  return "per instance";
}

/** The sentence the per-account hint adds when other environments of the organization run in the same AWS account. */
export function sharedAccountSentence(names: readonly string[]): string | undefined {
  if (names.length === 0) return undefined;
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `This AWS account also runs ${list}, so a per-account limit is shared with ${names.length === 1 ? "it" : "them"}.`;
}

/** AWS's request states as words. "Open" ones block a second request: AWS allows one per quota, and a Support case can take days. */
export const QUOTA_REQUEST_STATUS: Record<QuotaIncreaseStatus, { label: string; tone: "ok" | "warning" | "critical" | "neutral"; open: boolean }> = {
  PENDING: { label: "requested", tone: "warning", open: true },
  CASE_OPENED: { label: "case opened", tone: "warning", open: true },
  APPROVED: { label: "approved", tone: "ok", open: false },
  DENIED: { label: "denied", tone: "critical", open: false },
  NOT_APPROVED: { label: "not approved", tone: "critical", open: false },
  CASE_CLOSED: { label: "case closed", tone: "neutral", open: false },
  INVALID_REQUEST: { label: "invalid request", tone: "critical", open: false },
  UNKNOWN: { label: "unknown", tone: "neutral", open: true },
};

/**
 * A request's one status (the owner, 1 Oct): while it is open, whose move it
 * is - AWS action or customer action, from the Support case the customer's
 * poller reads - or plain "open" where that cannot be read (no Business
 * support, or not polled yet); once closed, AWS's answer. The case column
 * carries only the case number.
 */
export function requestStatus(r: Pick<QuotaIncreaseRequestView, "status" | "case_id" | "caseCourt">): { label: string; tone: "ok" | "warning" | "critical" | "neutral" } {
  const s = QUOTA_REQUEST_STATUS[r.status];
  if (!s.open) return s;
  if (r.case_id && r.caseCourt === "customer") return { label: "customer action", tone: "critical" };
  if (r.case_id && r.caseCourt === "aws") return { label: "AWS action", tone: "warning" };
  if (r.case_id && r.caseCourt === "closed") return { label: "closed", tone: "neutral" };
  return { label: "open", tone: "warning" };
}
